// src/components/auth/SigninHeader.jsx
import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import botablewhite from '../../images/botable-white.png';
import botableLogo from '../../images/Botable-Logo.png';

const SigninHeader = ({ isDarkMode, isLoggedIn = false }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [user, setUser] = useState({
    username: 'Guest',
    email: 'user@example.com',
    organization: 'Organization'
  });
  const [profileImageUrl, setProfileImageUrl] = useState(null);
  const dropdownRef = useRef(null);

  // Function to load user data and profile image
  const loadUserData = () => {
    const stored = localStorage.getItem('user');
    if (stored) {
      try {
        const userData = JSON.parse(stored);
        setUser(userData);
        
        // Set profile image URL if exists
        if (userData.profile_image_path) {
          const imageUrl = `http://localhost:5000/api/user/profile-image/${userData.profile_image_path}`;
          setProfileImageUrl(imageUrl);
        } else {
          setProfileImageUrl(null);
        }
      } catch (err) {
        console.error('Error parsing user from localStorage:', err);
        setProfileImageUrl(null);
      }
    } else {
      // No user data, reset to defaults
      setUser({
        username: 'Guest',
        email: 'user@example.com',
        organization: 'Organization'
      });
      setProfileImageUrl(null);
    }
  };

  // Load user data on mount and when isLoggedIn changes
  useEffect(() => {
    if (isLoggedIn) {
      loadUserData();
    } else {
      // Reset user data when not logged in
      setUser({
        username: 'Guest',
        email: 'user@example.com',
        organization: 'Organization'
      });
      setProfileImageUrl(null);
    }
  }, [isLoggedIn]);

  // Also reload user data when location changes (like after signin)
  useEffect(() => {
    if (isLoggedIn) {
      loadUserData();
    }
  }, [location.pathname, isLoggedIn]);

  // Listen for profile updates from Profile component
  useEffect(() => {
    const handleProfileUpdate = (event) => {
      const { user: updatedUser, imageUrl } = event.detail;
      setUser(updatedUser);
      setProfileImageUrl(imageUrl);
    };

    // Listen for localStorage changes (when user signs in from another tab or component updates localStorage)
    const handleStorageChange = (event) => {
      if (event.key === 'user' && isLoggedIn) {
        loadUserData();
      }
    };

    window.addEventListener('userProfileUpdated', handleProfileUpdate);
    window.addEventListener('storage', handleStorageChange);
    
    return () => {
      window.removeEventListener('userProfileUpdated', handleProfileUpdate);
      window.removeEventListener('storage', handleStorageChange);
    };
  }, [isLoggedIn]);

  // Close dropdown on outside click or Escape
  useEffect(() => {
    const handleClickOutside = e => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setDropdownOpen(false);
      }
    };
    const handleEscape = e => {
      if (e.key === 'Escape') setDropdownOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleEscape);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscape);
    };
  }, []);

  const getInitials = (firstName, lastName) => {
    if (!firstName && !lastName) return 'U';
    const first = firstName ? firstName.charAt(0).toUpperCase() : '';
    const last = lastName ? lastName.charAt(0).toUpperCase() : '';
    return first + last || 'U';
  };

  // Handle navigation with proper routing
  const handleNavigation = (path) => {
    setDropdownOpen(false);
    navigate(path);
  };

  // Handle sign out
  const handleSignOut = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    setDropdownOpen(false);
    
    // Reset state immediately
    setUser({
      username: 'Guest',
      email: 'user@example.com',
      organization: 'Organization'
    });
    setProfileImageUrl(null);
    
    navigate('/signin');
  };

  // Updated dropdown items with working navigation
  const dropdownItems = [
    { 
      icon: (
        <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M20 21V19C20 17.9391 19.5786 16.9217 18.8284 16.1716C18.0783 15.4214 17.0609 15 16 15H8C6.93913 15 5.92172 15.4214 5.17157 16.1716C4.42143 16.9217 4 17.9391 4 19V21" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          <circle cx="12" cy="7" r="4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
      ), 
      text: 'Profile', 
      action: () => handleNavigation('/profile')
    },
    { 
      icon: (
        <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M17 21V19C17 17.9391 16.5786 16.9217 15.8284 16.1716C15.0783 15.4214 14.0609 15 13 15H5C3.93913 15 2.92172 15.4214 2.17157 16.1716C1.42143 16.9217 1 17.9391 1 19V21" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          <circle cx="9" cy="7" r="4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          <path d="M23 21V19C22.9993 18.1137 22.7044 17.2528 22.1614 16.5523C21.6184 15.8519 20.8581 15.3516 20 15.13" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          <path d="M16 3.13C16.8604 3.35031 17.623 3.85071 18.1676 4.55232C18.7122 5.25392 19.0078 6.11683 19.0078 7.005C19.0078 7.89318 18.7122 8.75608 18.1676 9.45769C17.623 10.1593 16.8604 10.6597 16 10.88" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
      ), 
      text: 'Team Management', 
      action: () => handleNavigation('/team-management')
    },
    { divider: true },
    { 
      icon: (
        <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2"/>
          <path d="M9.09 9C9.3251 8.33167 9.78915 7.76811 10.4 7.40913C11.0108 7.05016 11.7289 6.91894 12.4272 7.03871C13.1255 7.15849 13.7588 7.52152 14.2151 8.06353C14.6713 8.60553 14.9211 9.29152 14.92 10C14.92 12 11.92 13 11.92 13" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          <path d="M12 17H12.01" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
      ), 
      text: 'Help & Support', 
      action: () => handleNavigation('/help-support')
    },
    { divider: true },
    {
      icon: (
        <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M9 21H5C4.46957 21 3.96086 20.7893 3.58579 20.4142C3.21071 20.0391 3 19.5304 3 19V5C3 4.46957 3.21071 3.96086 3.58579 3.58579C3.96086 3.21071 4.46957 3 5 3H9" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          <polyline points="16,17 21,12 16,7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          <line x1="21" y1="12" x2="9" y2="12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
      ), 
      text: 'Sign Out', 
      action: handleSignOut
    },
  ];

  return (
    <header className={`
      fixed top-0 left-0 w-full h-[70px] z-[100] shadow-sm
      transition-colors duration-300
      ${isDarkMode ? 'bg-header-dark' : 'bg-header-light'}
    `}>
      <div className="flex justify-between items-center h-full px-8">
        {/* Logo */}
        <img
          src={isDarkMode ? botablewhite : botableLogo}
          alt="Botable Logo"
          className="h-10 w-auto cursor-pointer"
          onClick={() => navigate('/mainscreen')}
        />

        {/* User menu */}
        {isLoggedIn && (
          <div className="flex items-center relative" ref={dropdownRef}>
            {/* Name & Org */}
            <div className="text-right mr-3">
              <div className={`text-base font-medium ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                {user.username}
              </div>
              <div className={`text-sm ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                {user.organization || user.agencyName || 'Organization'}
              </div>
            </div>

            {/* Avatar */}
            <button
              onClick={() => setDropdownOpen(o => !o)}
              className="w-11 h-11 bg-purple-500 hover:bg-purple-600 rounded-full
                         flex items-center justify-center text-white font-medium text-lg
                         transition-colors duration-200 overflow-hidden relative"
            >
              {profileImageUrl ? (
                <img 
                  src={profileImageUrl} 
                  alt="Profile" 
                  className="w-full h-full object-cover rounded-full"
                  onError={(e) => {
                    console.log('Profile image failed to load:', profileImageUrl);
                    // Fallback to initials if image fails to load
                    setProfileImageUrl(null);
                  }}
                />
              ) : (
                getInitials(user.firstName, user.lastName)
              )}
            </button>

            {/* Dropdown */}
            <div className={`
              absolute top-full right-0 mt-2.5
              w-72                      /* fixed 18rem / 288px width */
              max-h-[calc(100vh-80px)]  /* never overflow viewport */
              overflow-y-auto          /* scroll if too tall */
              ${isDarkMode ? 'bg-[#2D2D30] border-gray-600' : 'bg-white border-gray-200'}
              border rounded-xl shadow-lg
              transition-opacity duration-200
              ${dropdownOpen ? 'opacity-100 visible' : 'opacity-0 invisible'}
            `}>
              {/* Dropdown header */}
              <div className={`flex items-center p-5 border-b ${isDarkMode ? 'border-gray-600' : 'border-gray-200'}`}>
                <div className="w-12 h-12 bg-purple-500 rounded-full flex items-center justify-center text-white text-xl mr-4 overflow-hidden">
                  {profileImageUrl ? (
                    <img 
                      src={profileImageUrl} 
                      alt="Profile" 
                      className="w-full h-full object-cover rounded-full"
                      onError={(e) => {
                        console.log('Profile image failed to load in dropdown:', profileImageUrl);
                        // Fallback to initials if image fails to load
                        setProfileImageUrl(null);
                      }}
                    />
                  ) : (
                    getInitials(user.firstName, user.lastName)
                  )}
                </div>
                <div className="flex-grow">
                  <div className={`text-lg font-semibold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                    {user.firstName && user.lastName ? `${user.firstName} ${user.lastName}` : user.username}
                  </div>
                  <div className={`text-base ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                    {user.email}
                  </div>
                  <div className={`text-sm mt-1 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                    {user.agencyName || user.organization || 'Organization'}
                  </div>
                </div>
              </div>

              {/* Dropdown items */}
              <div className="py-2">
                {dropdownItems.map((item, i) => (
                  item.divider
                    ? <div key={i} className={`my-2 border-t ${isDarkMode ? 'border-gray-600' : 'border-gray-200'}`} />
                    : <button
                        key={i}
                        onClick={item.action}
                        className={`
                          w-full px-5 py-3 flex items-center text-left transition-colors duration-200
                          ${isDarkMode
                            ? 'text-gray-300 hover:bg-gray-700 hover:text-white'
                            : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'}
                        `}
                      >
                        <span className="w-5 h-5 mr-3 flex items-center justify-center text-sm">
                          {item.icon}
                        </span>
                        {item.text}
                      </button>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>
    </header>
  );
};

export default SigninHeader;